
/* ==========================================================================
   COMMUNITY BUILDS DRAWER ENGINE (/database/js/builds-drawer.js)
   ========================================================================== */

import { escapeHtml, sanitizeUrl, slugifyUrlSegment, caseRouteSlug } from '../utils.js?v=20260819-audit2';
import { fetchCaseCommunityBuilds } from '../api.js?v=20260819-phase35';
import { debugRuntimeIssue } from '../runtime.js?v=20260819-audit2';
import { state } from '../state.js?v=20260819-audit2';
import { getDocumentedBuildFilterOptions, documentedBuildMatchesFilters } from './ui-helpers.js?v=20260819-audit2';

function sanitizeRawUrl(url) {
  if (!url) return '#';
  const raw = String(url).trim();
  if (!raw) return '#';

  const hasExplicitScheme = /^[a-zA-Z][a-zA-Z\d+.-]*:/.test(raw);
  const isProtocolRelative = raw.startsWith('//');

  try {
    const baseOrigin = (typeof window !== 'undefined' && window.location?.origin)
      ? window.location.origin
      : 'https://sff.gg';
    const parsed = new URL(raw, baseOrigin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '#';

    if (!hasExplicitScheme && !isProtocolRelative) {
      if (parsed.origin !== baseOrigin) return '#';
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
    }

    return parsed.href;
  } catch (err) {
    debugRuntimeIssue('Build Drawer URL', err);
    return '#';
  }
}


function buildSummaryText(summary = {}) {
  const parts = [];
  const moboTypes = summary?.mobo_types && typeof summary.mobo_types === 'object' ? summary.mobo_types : {};
  for (const label of ['ITX', 'mATX', 'ATX', 'DTX']) {
    const count = Number(moboTypes[label] || 0);
    if (count > 0) parts.push(`${count} ${label}`);
  }

  const gpuCount = Number(summary?.unique_gpus || 0);
  const coolerCount = Number(summary?.unique_coolers || 0);
  if (gpuCount > 0) parts.push(`${gpuCount} UNIQUE GPU${gpuCount === 1 ? '' : 's'}`);
  if (coolerCount > 0) parts.push(`${coolerCount} COOLER${coolerCount === 1 ? '' : 's'}`);
  return parts.join(' · ') || 'DOCUMENTED HARDWARE';
}


function getCaseForBuildFilters(caseSlug) {
  return (state.fetchedCasesGlobal || []).find(caseObj => caseRouteSlug(caseObj) === caseSlug) || null;
}

function buildFilterControlsHtml(dimensions, totalBuilds) {
  if (totalBuilds < 2 || !Array.isArray(dimensions) || dimensions.length === 0) return '';

  return `
    <div class="sff-build-filter-bar" data-build-filter-root>
      <div class="sff-build-filter-head">
        <span class="sff-build-filter-title">FILTER BUILDS</span>
        <span class="sff-build-filter-status" data-build-filter-status>SHOWING ${totalBuilds} / ${totalBuilds}</span>
        <button type="button" class="sff-build-filter-clear" data-build-filter-clear>CLEAR</button>
      </div>
      <div class="sff-build-filter-grid">
        ${dimensions.map(dimension => `
          <label class="sff-build-filter-field">
            <span>${escapeHtml(dimension.label)}</span>
            <select class="sff-build-filter-select" data-build-filter-key="${escapeHtml(dimension.key)}" aria-label="Filter builds by ${escapeHtml(dimension.label.toLowerCase())}">
              <option value="all">ALL (${totalBuilds})</option>
              ${dimension.options.map(option => `
                <option value="${escapeHtml(option.value)}">${escapeHtml(option.label)} (${option.count})</option>
              `).join('')}
            </select>
          </label>
        `).join('')}
      </div>
      <div class="sff-build-filter-empty" data-build-filter-empty hidden>NO BUILDS MATCH THESE FILTERS.</div>
    </div>`;
}

function applyBuildDrawerFilters(drawer) {
  const root = drawer?.querySelector('[data-build-filter-root]');
  if (!root) return;

  const activeFilters = {};
  root.querySelectorAll('[data-build-filter-key]').forEach(select => {
    activeFilters[select.getAttribute('data-build-filter-key')] = select.value || 'all';
  });

  const cards = Array.from(drawer.querySelectorAll('.community-build-card'));
  let shown = 0;
  cards.forEach(card => {
    let descriptor = {};
    try {
      descriptor = JSON.parse(card.getAttribute('data-build-filters') || '{}');
    } catch (err) {
      debugRuntimeIssue('Build Filter Descriptor', err);
    }
    const matches = documentedBuildMatchesFilters(descriptor, activeFilters);
    if (matches) {
      card.style.removeProperty('display');
      shown += 1;
    } else {
      card.style.setProperty('display', 'none', 'important');
    }
  });

  const status = root.querySelector('[data-build-filter-status]');
  if (status) status.textContent = `SHOWING ${shown} / ${cards.length}`;
  const empty = root.querySelector('[data-build-filter-empty]');
  if (empty) empty.hidden = shown !== 0;
}

function bindBuildDrawerFilters(drawer) {
  const root = drawer?.querySelector('[data-build-filter-root]');
  if (!root || root.getAttribute('data-bound') === '1') return;
  root.setAttribute('data-bound', '1');

  root.addEventListener('change', event => {
    if (!event.target.closest('[data-build-filter-key]')) return;
    applyBuildDrawerFilters(drawer);
  });

  const clear = root.querySelector('[data-build-filter-clear]');
  if (clear) {
    clear.addEventListener('click', event => {
      event.preventDefault();
      root.querySelectorAll('[data-build-filter-key]').forEach(select => { select.value = 'all'; });
      applyBuildDrawerFilters(drawer);
    });
  }
}


export async function toggleBuildsDrawer(buildsBtn) {
  const caseSlug = buildsBtn.getAttribute('data-slug');
  const drawerId = buildsBtn.getAttribute('data-target');
  const drawer = document.getElementById(drawerId);
  if (!drawer) return;
  buildsBtn.setAttribute('aria-controls', drawerId);

  const isOpen = drawer.style.display === 'block';

  if (isOpen) {
    drawer.style.display = 'none';
    drawer.setAttribute('aria-hidden', 'true');
    buildsBtn.classList.remove('active');
    buildsBtn.setAttribute('aria-expanded', 'false');
    return;
  }

  drawer.style.display = 'block';
  drawer.setAttribute('aria-hidden', 'false');
  buildsBtn.classList.add('active');
  buildsBtn.setAttribute('aria-expanded', 'true');

  if (drawer.innerHTML.trim() === "") {
    drawer.innerHTML = `<div style="text-align:center; padding:12px; color:#71717a; font-size:11px;">FETCHING COMMUNITY BUILDS...</div>`;

    const data = await fetchCaseCommunityBuilds(caseSlug, 1, 50);

    if (!data || !data.builds || data.builds.length === 0) {
      drawer.innerHTML = `<div style="text-align:center; padding:12px; color:#71717a; font-size:11px;">NO DOCUMENTED COMMUNITY BUILDS YET FOR THIS CASE.</div>`;
      return;
    }

    const caseObj = getCaseForBuildFilters(caseSlug);
    const filterModel = getDocumentedBuildFilterOptions(data.builds, caseObj);
    const filterMarkup = buildFilterControlsHtml(filterModel.dimensions, data.builds.length);

    let html = `
      <div class="community-summary-bar" style="display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; padding-bottom:8px; border-bottom:1px dashed rgba(255,255,255,0.1); font-size:10.5px;">
        <span style="color:#38bdf8; font-weight:800;">USER BUILDS (${Number(data.summary?.total_builds || data.builds.length)})</span>
        <span style="color:#71717a;">${escapeHtml(buildSummaryText(data.summary))}</span>
      </div>
      ${filterMarkup}
      <div class="community-builds-grid">
    `;

    data.builds.forEach((b, buildIndex) => {
      const rawPhotos = (b.photos && b.photos.length > 0)
        ? b.photos
        : [{ full_url: b.thumbnail, thumb_url: b.thumbnail }];
      const photos = rawPhotos.map(photo => ({
        ...photo,
        full_url: sanitizeRawUrl(photo?.full_url),
        thumb_url: sanitizeRawUrl(photo?.thumb_url)
      }));

      const photoBadge = photos.length > 1
        ? `<span class="community-photo-count"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:10px;height:10px;"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg> 1/${photos.length}</span>`
        : '';

      const navControls = photos.length > 1 ? `
        <button type="button" class="community-card-nav community-nav-prev" data-dir="prev" title="Previous photo">&lsaquo;</button>
        <button type="button" class="community-card-nav community-nav-next" data-dir="next" title="Next photo">&rsaquo;</button>
      ` : '';

      const thumbStrip = photos.length > 1 ? `
        <div class="community-thumb-strip">
          ${photos.map((p, i) => `
            <button type="button" class="community-thumb-dot ${i === 0 ? 'active' : ''}" data-index="${i}">
              <img src="${sanitizeUrl(p.thumb_url)}" alt="Thumbnail ${i + 1}" />
            </button>
          `).join('')}
        </div>
      ` : '';

      const jsonPhotosAttr = escapeHtml(JSON.stringify(photos));
      const buildFilterDescriptor = filterModel.descriptors[buildIndex] || {};
      const jsonBuildFiltersAttr = escapeHtml(JSON.stringify(buildFilterDescriptor));
      const rawLoadConfig = (b.load_config && typeof b.load_config === 'object') ? b.load_config : {};
      const rawComponents = rawLoadConfig.components && Object.keys(rawLoadConfig.components).length
        ? rawLoadConfig.components
        : (b.evidence_config?.components || {});
      const loadConfig = {
        ...rawLoadConfig,
        case_slug: rawLoadConfig.case_slug || data.case_slug || '',
        case_catalog_id: rawLoadConfig.case_catalog_id || data.case_catalog_id || '',
        case_mode: b.evidence_config?.case_mode || rawLoadConfig.case_mode || '',
        case_fixed_step: b.evidence_config?.case_fixed_step || rawLoadConfig.case_fixed_step || rawLoadConfig.fixedStep || '',
        mobo_form_factor: rawLoadConfig.mobo_form_factor || b.evidence_config?.mobo_form_factor || '',
        components: rawComponents
      };
      const jsonBuildConfigAttr = escapeHtml(JSON.stringify(loadConfig));

      html += `
        <div class="community-build-card font-mono" data-photos="${jsonPhotosAttr}" data-build-filters="${jsonBuildFiltersAttr}" data-active-index="0">
          <div class="community-card-cover-wrapper">
            <a href="${sanitizeUrl(photos[0].full_url)}" target="_blank" rel="noopener noreferrer" class="community-card-lightbox-anchor" title="Click to view full photo">
              <img class="community-card-cover-img" src="${sanitizeUrl(photos[0].thumb_url)}" alt="${escapeHtml(b.title)}" onerror="this.onerror=null;this.src='/assets/images/placeholder.webp';" />
            </a>
            ${photoBadge}
            ${navControls}
          </div>
          
          ${thumbStrip}

          <a href="${sanitizeUrl(b.permalink)}" target="_blank" rel="noopener noreferrer" class="community-card-link">
            <div class="community-card-body">
              <h4 class="community-card-title">${escapeHtml(b.title)}</h4>
              
              <div class="community-specs-matrix">
                <div class="community-spec-row">
                  <span class="community-spec-lbl">CPU:</span>
                  <span class="community-spec-val">${escapeHtml(b.cpu)}</span>
                </div>
                <div class="community-spec-row">
                  <span class="community-spec-lbl">GPU:</span>
                  <span class="community-spec-val">${escapeHtml(b.gpu)}</span>
                </div>
              </div>
              
              <div class="community-card-footer">
                By ${escapeHtml(b.builder)} · ${escapeHtml(b.date)}
              </div>
            </div>
          </a>
          <button type="button" class="community-load-config-btn" data-build-id="${Number(b.build_id || 0)}" data-build-config="${jsonBuildConfigAttr}" style="width:calc(100% - 20px);margin:0 10px 10px;padding:7px 8px;border:1px solid rgba(56,189,248,.35);background:rgba(56,189,248,.08);color:#38bdf8;font:800 9px/1.2 'JetBrains Mono',monospace;letter-spacing:.04em;cursor:pointer;">
            LOAD IN CONFIGURATOR
          </button>
        </div>
      `;
    });

    html += `</div>`;
    drawer.innerHTML = html;
    bindBuildDrawerFilters(drawer);
  }
}


export async function handleLoadBuildClick(loadBtn) {
  if (!loadBtn) return false;
  let config = null;
  try {
    config = JSON.parse(loadBtn.getAttribute('data-build-config') || 'null');
  } catch (err) {
    debugRuntimeIssue('Load Community Build', err);
    return false;
  }
  if (!config || typeof config !== 'object') return false;

  const loader = window.loadCommunityBuildIntoConfigurator;
  if (typeof loader !== 'function') {
    debugRuntimeIssue('Load Community Build', new Error('Configurator loader is unavailable'));
    return false;
  }

  const originalText = loadBtn.textContent;
  loadBtn.disabled = true;
  loadBtn.textContent = 'LOADING...';
  try {
    const loaded = await loader(config);
    loadBtn.textContent = loaded ? 'LOADED ✓' : 'PARTIAL LOAD';
    setTimeout(() => {
      loadBtn.disabled = false;
      loadBtn.textContent = originalText;
    }, 1800);
    return !!loaded;
  } catch (err) {
    debugRuntimeIssue('Load Community Build', err);
    loadBtn.textContent = 'LOAD FAILED';
    setTimeout(() => {
      loadBtn.disabled = false;
      loadBtn.textContent = originalText;
    }, 1800);
    return false;
  }
}

export function handleCardThumbnailClick(thumbDot) {
  const card = thumbDot.closest('.community-build-card');
  if (!card) return;

  const photosData = JSON.parse(card.getAttribute('data-photos') || '[]');
  const index = parseInt(thumbDot.getAttribute('data-index') || '0', 10);
  if (!photosData[index]) return;

  card.setAttribute('data-active-index', index);

  const imgEl = card.querySelector('.community-card-cover-img');
  const anchorEl = card.querySelector('.community-card-lightbox-anchor');
  const countEl = card.querySelector('.community-photo-count');

  if (imgEl) imgEl.src = sanitizeRawUrl(photosData[index].thumb_url);
  if (anchorEl) anchorEl.href = sanitizeRawUrl(photosData[index].full_url);
  if (countEl) {
    countEl.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:10px;height:10px;"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg> ${index + 1}/${photosData.length}`;
  }

  card.querySelectorAll('.community-thumb-dot').forEach((dot, idx) => {
    dot.classList.toggle('active', idx === index);
  });
}

export function handleCardNavClick(navBtn) {
  const card = navBtn.closest('.community-build-card');
  if (!card) return;

  const photosData = JSON.parse(card.getAttribute('data-photos') || '[]');
  if (photosData.length <= 1) return;

  let currentIndex = parseInt(card.getAttribute('data-active-index') || '0', 10);
  const dir = navBtn.getAttribute('data-dir');

  if (dir === 'next') {
    currentIndex = (currentIndex + 1) % photosData.length;
  } else {
    currentIndex = (currentIndex - 1 + photosData.length) % photosData.length;
  }

  card.setAttribute('data-active-index', currentIndex);

  const imgEl = card.querySelector('.community-card-cover-img');
  const anchorEl = card.querySelector('.community-card-lightbox-anchor');
  const countEl = card.querySelector('.community-photo-count');

  if (imgEl) imgEl.src = sanitizeRawUrl(photosData[currentIndex].thumb_url);
  if (anchorEl) anchorEl.href = sanitizeRawUrl(photosData[currentIndex].full_url);
  if (countEl) {
    countEl.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:10px;height:10px;"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg> ${currentIndex + 1}/${photosData.length}`;
  }

  card.querySelectorAll('.community-thumb-dot').forEach((dot, idx) => {
    dot.classList.toggle('active', idx === currentIndex);
  });
}
